import { useCallback, useMemo } from 'react'

import { CreatorPermission } from '@api'
import { useUnit } from 'effector-react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'

import { Page } from '@/components/layout/page/page'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/components/ui/toast'
import { $creators } from '@/models/creators.ts'
import { useTeamPermissions } from '@/modules/team/hooks/use-team-permissions'
import { AppRoutes } from '@/utils/constants'
import { handleError } from '@/utils/error'

import { useUpdateTeamRoleTable } from '../../hooks/use-update-team-role'
import type {
  TeamCreateRoleRequest,
  TeamRole,
  TeamRolePermissionAssignment,
} from '../../types'
import type { PermissionsTableFormFields } from '../../ui/permissions-table-form'
import { PermissionsTableField } from '../../ui/permissions-table-form'
import { TeamRoleForm } from '../../ui/roles/team-role-form'
import { transformToPermissionBlock } from '../../utils'

interface Props {
  role: TeamRole
}

type FormFields = PermissionsTableFormFields & {
  name: string
  memberIds: number[]
}

export const TeamRoleEdit = ({ role }: Props) => {
  const navigate = useNavigate()
  const toast = useToast()

  const {
    allPermissions,
    accessOnlyFansProfilePermission,
    generalPermissions,
    isFetched,
    isFetching,
    ofPermissions,
  } = useTeamPermissions()
  const creators = useUnit($creators)
  const { assignPermissions, editRole } = useUpdateTeamRoleTable()

  const isDefaultRole = role.isDefault
  const defaultValues = useMemo(() => {
    const rolePermissions = transformToPermissionBlock(
      role.permissions,
      creators,
    )

    return {
      permissions: rolePermissions,
    }
  }, [creators, role.permissions])

  const form = useForm<FormFields>({
    defaultValues: {
      name: role.name,
      memberIds: role.members.map((member) => member.id),
      permissionsTable: {
        ...defaultValues,
      },
    },
  })
  const {
    control,
    formState: { dirtyFields },
  } = form

  const handleCancel = () => {
    navigate(AppRoutes.Teams.Roles.List.asUrl())
  }

  const handleSubmit = useCallback(
    async (values: FormFields) => {
      try {
        const permissionsToUpdate: TeamRolePermissionAssignment[] = []

        const { name, memberIds } = values

        values.permissionsTable.permissions.forEach((creator) => {
          const { creatorId, type, permissions } = creator

          Object.keys(permissions).forEach((permission) => {
            if (permissions[permission as CreatorPermission]) {
              // Skip OF_ permissions if the user does not have access to the OnlyFans profile
              if (
                permission.startsWith('OF_') &&
                !permissions.ACCESS_ONLYFANS_PROFILE
              ) {
                return
              }
              permissionsToUpdate.push({
                permission: permission as CreatorPermission,
                creatorId: creatorId,
                isAssignAllCreators: type === 'SUPER_CREATOR',
              })
            }
          })
        })

        if (dirtyFields.name || dirtyFields.memberIds) {
          const data: Partial<TeamCreateRoleRequest> = {}

          if (dirtyFields.name) {
            data.name = name
          }

          if (dirtyFields.memberIds) {
            data.memberIds = memberIds
          }
          await editRole.mutateAsync({
            newRoleDetails: data,
            roleId: role.id,
          })
        }

        await assignPermissions.mutateAsync({
          newPermissionDetails: { permissions: permissionsToUpdate },
          roleId: role.id,
        })

        navigate(AppRoutes.Teams.Roles.List.asUrl())
        toast.success(`Successfully updated role details for "${role.name}"!`)
      } catch (error) {
        handleError(
          error,
          'Something went wrong while updating the role permissions. Try again later!',
        )
      }
    },
    [
      assignPermissions,
      dirtyFields.memberIds,
      dirtyFields.name,
      editRole,
      navigate,
      role.id,
      role.name,
      toast,
    ],
  )

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Page
          title="Edit Role"
          backToPage={{
            name: 'Roles',
            url: AppRoutes.Teams.Roles.List.asUrl(),
          }}
          headerOptions={
            <div className="flex gap-2">
              <Button
                onClick={handleCancel}
                type="button"
                variant="outline"
                className="w-32"
              >
                Cancel
              </Button>
              <Button
                onClick={form.handleSubmit(handleSubmit)}
                type="button"
                variant="default"
                className="w-32"
                disabled={!form.formState.isDirty}
                isLoading={editRole.isLoading || assignPermissions.isLoading}
              >
                Save
              </Button>
            </div>
          }
        >
          <div className="flex w-full flex-col gap-10 overflow-hidden">
            <Card>
              <CardHeader>
                <CardTitle>General Info</CardTitle>
              </CardHeader>
              <CardContent>
                <TeamRoleForm isDisabled={isDefaultRole} />
              </CardContent>
            </Card>
            <Card className="relative">
              <CardHeader>
                <CardTitle>Permissions</CardTitle>
              </CardHeader>
              <CardContent isLoading={!isFetched && isFetching}>
                <PermissionsTableField
                  allPermissions={allPermissions || []}
                  generalPermissions={generalPermissions}
                  isSuperCreatorEnabled
                  ofPermissions={ofPermissions}
                  control={control}
                  accessOnlyFansProfilePermission={
                    accessOnlyFansProfilePermission
                  }
                  isEditEnabled={!role.isDefault}
                />
              </CardContent>
            </Card>
          </div>
        </Page>
      </form>
    </FormProvider>
  )
}
